import { FormEvent, Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { Modal } from '../components/Modal';
import { MultiSelect } from '../components/MultiSelect';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { useToast } from '../components/Toast';
import type { BatchGrid, ExamPeriodDto, ExamScheduleDto, Staff } from '../types/models';
import { StatusPill, fmtTime, statusOf, toLocalInput } from './examScheduleShared';

const emptyForm = {
  subjectName: '', subjectCode: '',
  scheduledStart: '', scheduledEnd: '', durationMinutes: 90,
  invigilator: '', room: '',
};

/** "2024-06-01" -> "01-06-2024". */
function fmtDay(iso: string) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}
const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });

/** Detail page for one exam period: its subjects, and a form to add subjects within the date range. */
export function ExamPeriodDetailPage() {
  const { orgId = '' } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const periodId = params.get('id') ?? '';

  const [period, setPeriod] = useState<ExamPeriodDto | null>(null);
  const [subjects, setSubjects] = useState<ExamScheduleDto[] | null>(null);
  const [batches, setBatches] = useState<BatchGrid[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [fDept, setFDept] = useState('');
  const [fProgram, setFProgram] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editBatchId, setEditBatchId] = useState('');
  const [editBatchLabel, setEditBatchLabel] = useState('');
  // Create-mode multi-selections (empty at a level = all at that level).
  const [selDepts, setSelDepts] = useState<string[]>([]);
  const [selPrograms, setSelPrograms] = useState<string[]>([]);
  const [selBatches, setSelBatches] = useState<string[]>([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId, periodId]);

  async function refresh() {
    try {
      const [p, s, b, st] = await Promise.all([
        api.getExamPeriod(periodId), api.listExamSchedules(orgId, periodId),
        api.listAllBatches(orgId), api.listStaff(orgId),
      ]);
      setPeriod(p); setSubjects(s); setBatches(b); setStaff(st);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load exam.');
    }
  }

  // Batches belonging to this exam's batch year — the pool subjects can be scheduled for.
  const yearBatches = useMemo(
    () => period ? batches.filter(b => b.batchYearId === period.batchYearId) : [],
    [batches, period]);

  // ----- Create-form multi-selects (each level narrowed by the levels above; empty = all) -----
  const deptOptions = useMemo(() => {
    const m = new Map<string, string>();
    yearBatches.forEach(b => m.set(b.departmentId, `${b.departmentCode} — ${b.departmentName}`));
    return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [yearBatches]);
  const programOptions = useMemo(() => {
    const m = new Map<string, string>();
    yearBatches.filter(b => !selDepts.length || selDepts.includes(b.departmentId))
      .forEach(b => m.set(b.programId, `${b.programCode} — ${b.programName}`));
    return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [yearBatches, selDepts]);
  const batchSelectOptions = useMemo(() => yearBatches.filter(b =>
    (!selDepts.length || selDepts.includes(b.departmentId))
    && (!selPrograms.length || selPrograms.includes(b.programId)))
    .map(b => ({ value: b.id, label: `${b.code} — ${b.name}` })), [yearBatches, selDepts, selPrograms]);

  // Drop selections that are no longer valid when an upstream level changes.
  useEffect(() => {
    setSelPrograms(prev => prev.filter(id => programOptions.some(o => o.value === id)));
  }, [programOptions]);
  useEffect(() => {
    setSelBatches(prev => prev.filter(id => batchSelectOptions.some(o => o.value === id)));
  }, [batchSelectOptions]);

  // Batches a create targets: the batch year, narrowed by the department/program/batch selections.
  const targetBatches = useMemo(() => {
    if (editId) return [];
    return yearBatches.filter(b =>
      (!selDepts.length || selDepts.includes(b.departmentId))
      && (!selPrograms.length || selPrograms.includes(b.programId))
      && (!selBatches.length || selBatches.includes(b.id)));
  }, [editId, yearBatches, selDepts, selPrograms, selBatches]);

  // ----- Grid filters -----
  const gridDeptOptions = useMemo(() => {
    const m = new Map<string, string>();
    (subjects ?? []).forEach(r => m.set(r.departmentId, r.departmentName));
    return [...m].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [subjects]);
  const gridProgramOptions = useMemo(() => {
    const m = new Map<string, string>();
    (subjects ?? []).filter(r => !fDept || r.departmentId === fDept).forEach(r => m.set(r.programId, r.programName));
    return [...m].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [subjects, fDept]);
  // Sorted department-first so each department's subjects sit together (chronological within a
  // department); when "All departments" is selected the grid renders one block per department.
  const rows = useMemo(() => (subjects ?? [])
    .filter(r => (!fDept || r.departmentId === fDept) && (!fProgram || r.programId === fProgram))
    .sort((a, b) => a.departmentName.localeCompare(b.departmentName)
      || a.scheduledStart.localeCompare(b.scheduledStart)
      || `${a.programName}${a.batchCode}`.localeCompare(`${b.programName}${b.batchCode}`)),
    [subjects, fDept, fProgram]);

  // Per-department subject counts (for the group header) and serial numbers that restart in each
  // department block; when a single department is filtered, numbering is one continuous run.
  const deptCounts = useMemo(() => {
    const m: Record<string, number> = {};
    rows.forEach(r => { m[r.departmentId] = (m[r.departmentId] ?? 0) + 1; });
    return m;
  }, [rows]);
  const serials = useMemo(() => {
    const m = new Map<string, number>();
    const counters: Record<string, number> = {};
    rows.forEach(r => {
      const key = fDept ? '_all' : r.departmentId;
      counters[key] = (counters[key] ?? 0) + 1;
      m.set(r.id, counters[key]);
    });
    return m;
  }, [rows, fDept]);

  const staffNames = useMemo(() => {
    const names = staff.map(s => `${s.firstName} ${s.lastName}`.trim()).filter(Boolean);
    if (form.invigilator && !names.includes(form.invigilator)) names.unshift(form.invigilator);
    return names;
  }, [staff, form.invigilator]);

  function setField(key: keyof typeof emptyForm, value: string | number) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function openCreate() {
    if (!period) return;
    setFormError(null); setEditId(null);
    setSelDepts([]); setSelPrograms([]); setSelBatches([]);
    setForm({ ...emptyForm, scheduledStart: `${period.fromDate}T09:00`, scheduledEnd: `${period.fromDate}T12:00` });
    setShowModal(true);
  }
  function openEdit(r: ExamScheduleDto) {
    setFormError(null); setEditId(r.id);
    setEditBatchId(r.batchId);
    setEditBatchLabel(`${r.departmentName} · ${r.batchCode} — ${r.batchName}`);
    setForm({
      subjectName: r.subjectName, subjectCode: r.subjectCode ?? '',
      scheduledStart: toLocalInput(r.scheduledStart), scheduledEnd: toLocalInput(r.scheduledEnd),
      durationMinutes: r.durationMinutes, invigilator: r.invigilator ?? '', room: r.room ?? '',
    });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!period) return;
    setFormError(null);
    // Enforce the subject window falls within the exam period's From/To dates.
    const sd = form.scheduledStart.slice(0, 10), ed = form.scheduledEnd.slice(0, 10);
    if (sd < period.fromDate || ed > period.toDate) {
      setFormError(`Dates must be between ${fmtDay(period.fromDate)} and ${fmtDay(period.toDate)}.`);
      return;
    }
    const base = {
      subjectName: form.subjectName.trim(),
      subjectCode: form.subjectCode.trim() || null,
      examName: period.examName,
      scheduledStart: new Date(form.scheduledStart).toISOString(),
      scheduledEnd: new Date(form.scheduledEnd).toISOString(),
      durationMinutes: Number(form.durationMinutes),
      invigilator: form.invigilator.trim() || null,
      room: form.room.trim() || null,
      examPeriodId: period.id,
    };
    setBusy(true);
    try {
      if (editId) {
        await api.updateExamSchedule(editId, { ...base, batchId: editBatchId });
        toast.success('Subject updated.');
      } else {
        if (targetBatches.length === 0) { setFormError('Select at least one batch (or leave the boxes empty for all).'); setBusy(false); return; }
        for (const b of targetBatches) await api.createExamSchedule({ ...base, batchId: b.id });
        toast.success(`Added “${base.subjectName}” for ${targetBatches.length} batch${targetBatches.length === 1 ? '' : 'es'}.`);
      }
      setShowModal(false);
      await refresh();
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save subject.');
    } finally { setBusy(false); }
  }

  async function togglePublish(r: ExamScheduleDto) {
    try {
      await api.publishExamSchedule(r.id, !r.isPublished);
      await refresh();
      toast.success(r.isPublished ? 'Unpublished — hidden from students.' : 'Published to students.');
    } catch (e: any) { toast.error(e?.message ?? 'Failed to update.'); }
  }
  async function remove(r: ExamScheduleDto) {
    if (!confirm(`Delete the subject “${r.subjectName}” (${r.batchCode})? This cannot be undone.`)) return;
    try {
      await api.deleteExamSchedule(r.id);
      await refresh();
      toast.success('Subject deleted.');
    } catch (e: any) { toast.error(e?.message ?? 'Failed to delete.'); }
  }
  async function publishAll(publish: boolean) {
    try {
      for (const r of (subjects ?? [])) if (r.isPublished !== publish) await api.publishExamSchedule(r.id, publish);
      await refresh();
      toast.success(publish ? 'Published to students.' : 'Unpublished — hidden from students.');
    } catch (e: any) { toast.error(e?.message ?? 'Failed to update.'); }
  }

  const pub = (subjects ?? []).filter(r => r.isPublished).length;
  const total = (subjects ?? []).length;

  return (
    <OrgLayout>
      <div style={{ marginBottom: 8 }}>
        <Link to={`/org/${orgId}/exam-schedule`} className="link-btn">← Exam Schedule</Link>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: 0 }}>{period?.examName ?? 'Exam'}</h1>
          <div className="muted" style={{ marginTop: 4 }}>
            {period
              ? `${period.batchYearName} · ${fmtDay(period.fromDate)} → ${fmtDay(period.toDate)} · ${total} subject${total === 1 ? '' : 's'}`
              : 'Loading…'}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {total > 0 && pub < total && <button onClick={() => publishAll(true)}>Publish all</button>}
          {pub > 0 && <button onClick={() => publishAll(false)}>Unpublish all</button>}
          <button className="primary" onClick={openCreate} disabled={!period || yearBatches.length === 0}>+ Add subject</button>
        </div>
      </div>

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}
      {period && yearBatches.length === 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            This batch year has no batches yet — add batches on the{' '}
            <Link to={`/org/${orgId}/batches`}>Batch</Link> page before adding subjects.
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Department</div>
            <select value={fDept} onChange={e => { setFDept(e.target.value); setFProgram(''); }}>
              <option value="">All departments</option>
              {gridDeptOptions.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Program</div>
            <select value={fProgram} onChange={e => setFProgram(e.target.value)}>
              <option value="">All programs</option>
              {gridProgramOptions.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          {(fDept || fProgram) && <button onClick={() => { setFDept(''); setFProgram(''); }}>Clear</button>}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Subject</th>
              <th>Department</th>
              <th>Program</th>
              <th>Batch</th>
              <th>Date &amp; Window</th>
              <th>Duration</th>
              <th>Invigilator</th>
              <th>Status</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!subjects && <tr><td colSpan={10} className="muted">Loading…</td></tr>}
            {subjects && rows.length === 0 && (
              <tr><td colSpan={10} className="muted">
                {total === 0 ? 'No subjects yet — click “Add subject”.' : 'No subjects match the filters.'}
              </td></tr>
            )}
            {rows.map((r, idx) => {
              // Start a new department block whenever the department changes (only when unfiltered).
              const showHeader = !fDept && (idx === 0 || rows[idx - 1].departmentId !== r.departmentId);
              return (
                <Fragment key={r.id}>
                  {showHeader && (
                    <tr className="group-row">
                      <td colSpan={10}>
                        {r.departmentName}
                        <span className="muted" style={{ fontWeight: 400 }}>
                          {' · '}{deptCounts[r.departmentId]} subject{deptCounts[r.departmentId] === 1 ? '' : 's'}
                        </span>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>{serials.get(r.id)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.subjectName}</div>
                      {r.subjectCode && <div className="muted slug">{r.subjectCode}</div>}
                    </td>
                    <td>{r.departmentName}</td>
                    <td>{r.programName}</td>
                    <td><span className="batch-chip">{r.batchCode}</span></td>
                    <td>
                      <div>{fmtDateTime(r.scheduledStart)}</div>
                      <div className="muted">{fmtTime(r.scheduledStart)} → {fmtTime(r.scheduledEnd)}</div>
                    </td>
                    <td>{r.durationMinutes} min</td>
                    <td>
                      {r.invigilator || <span className="muted">Not assigned</span>}
                      {r.room && <div className="muted">{r.room}</div>}
                    </td>
                    <td><StatusPill st={statusOf(r, Date.now())} /></td>
                    <td>
                      <button className="link-btn" onClick={() => nav(`/org/${orgId}/exams/new?fromSchedule=${r.id}`)}>
                        Create questions
                      </button>
                      <button className="link-btn" onClick={() => openEdit(r)}>Edit</button>
                      <button className="link-btn" onClick={() => togglePublish(r)}>
                        {r.isPublished ? 'Unpublish' : 'Publish'}
                      </button>
                      <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(r)}>Delete</button>
                      <RecordInfoButton entity="exam-schedules" id={r.id} />
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && period && (
        <Modal title={editId ? 'Edit subject' : 'Add subject'} onClose={() => setShowModal(false)} width={720}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            {editId ? (
              <label>
                <div className="muted">Batch</div>
                <input value={editBatchLabel} disabled />
              </label>
            ) : (
              <>
                <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="muted">Department</div>
                    <div style={{ marginTop: 4 }}>
                      <MultiSelect options={deptOptions} selected={selDepts} onChange={setSelDepts}
                                   placeholder="All departments" emptyText="No departments" />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="muted">Program</div>
                    <div style={{ marginTop: 4 }}>
                      <MultiSelect options={programOptions} selected={selPrograms} onChange={setSelPrograms}
                                   placeholder="All programs" emptyText="No programs" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="muted">Batch</div>
                  <div style={{ marginTop: 4 }}>
                    <MultiSelect options={batchSelectOptions} selected={selBatches} onChange={setSelBatches}
                                 placeholder="All batches" emptyText="No batches" />
                  </div>
                </div>
                <div className="muted" style={{ fontSize: 12, marginTop: -4 }}>
                  Pick one or more at each level (leave a box empty to include all). This subject will be added for{' '}
                  <strong>{targetBatches.length}</strong> batch{targetBatches.length === 1 ? '' : 'es'}.
                </div>
              </>
            )}
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1, minWidth: 0 }}>
                <div className="muted">Subject name *</div>
                <input value={form.subjectName} required maxLength={300}
                       onChange={e => setField('subjectName', e.target.value)} placeholder="Database Management Systems" />
              </label>
              <label style={{ flex: '0 0 180px' }}>
                <div className="muted">Subject code</div>
                <input value={form.subjectCode} maxLength={100}
                       onChange={e => setField('subjectCode', e.target.value)} placeholder="CS23401" />
              </label>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1, minWidth: 0 }}>
                <div className="muted">Window opens *</div>
                <input type="datetime-local" value={form.scheduledStart} required
                       min={`${period.fromDate}T00:00`} max={`${period.toDate}T23:59`}
                       onChange={e => setField('scheduledStart', e.target.value)} />
              </label>
              <label style={{ flex: 1, minWidth: 0 }}>
                <div className="muted">Window closes *</div>
                <input type="datetime-local" value={form.scheduledEnd} required
                       min={`${period.fromDate}T00:00`} max={`${period.toDate}T23:59`}
                       onChange={e => setField('scheduledEnd', e.target.value)} />
              </label>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: -4 }}>
              Must be within {fmtDay(period.fromDate)} → {fmtDay(period.toDate)}.
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 140px' }}>
                <div className="muted">Duration (min) *</div>
                <input type="number" min={1} value={form.durationMinutes} required
                       onChange={e => setField('durationMinutes', Number(e.target.value))} />
              </label>
              <label style={{ flex: 1, minWidth: 0 }}>
                <div className="muted">Invigilator</div>
                <select value={form.invigilator} onChange={e => setField('invigilator', e.target.value)}>
                  <option value="">— Not assigned —</option>
                  {staffNames.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label style={{ flex: '0 0 160px' }}>
                <div className="muted">Room</div>
                <input value={form.room} maxLength={100}
                       onChange={e => setField('room', e.target.value)} placeholder="Room 204" />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Add subject'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
